import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { CapacityBar } from './CapacityBar'
import { PortMapVisual } from './PortMapVisual'

interface DeviceRow {
  id: string
  label: string | null
  spec_brand: string | null
  spec_model: string | null
  spec_category: string | null
  spec_total_ports: number | null
  spec_poe_budget_watts: number | null
  spec_poe_draw_watts: number | null
  spec_max_camera_channels: number | null
  spec_storage_tb: number | null
  actual_storage_tb: number | null
  channels_used: number | null
  storage_used_tb: number | null
  // Synced device fields (fallback when no device_spec linked)
  device_type: string | null
  device_model: string | null
  hostname: string | null
  port_count: number | null
  poe_budget_watts: number | null
}

interface PortRow {
  port_number: number
  status: string
  connected_device_id: string | null
  connected_device_label?: string | null
  connected_spec_category?: string | null
  poe_watts_draw: number | null
  connected_poe_draw: number | null
}

interface CapacityData {
  devices: DeviceRow[]
  switches: DeviceRow[]
  switchPorts: Record<string, PortRow[]>
  cameras: DeviceRow[]
  nvrs: DeviceRow[]
}

interface Props {
  siteId: string
  onOpenDevice?: (slug: string) => void
}

function calculateRetention(totalTB: number, usedTB: number, cameras: number, mainBitrateMbps = 8, subBitrateMbps = 2) {
  const availableBytes = (totalTB - usedTB) * 1024 * 1024 * 1024 * 1024
  const totalBitrate = cameras * (mainBitrateMbps + subBitrateMbps)
  const bytesPerSecond = (totalBitrate * 1000000) / 8
  const bytesPerDay = bytesPerSecond * 86400
  const retentionDays = bytesPerDay > 0 ? availableBytes / bytesPerDay : 0
  const dailyGB = bytesPerDay / (1024 * 1024 * 1024)
  return { retentionDays, dailyGB }
}

function calculatePoeBudget(totalBudget: number, ports: PortRow[]) {
  const totalDraw = ports.reduce((sum: number, p: PortRow) => sum + (p.poe_watts_draw || p.connected_poe_draw || 0), 0)
  const available = totalBudget - totalDraw
  const headroom = totalBudget > 0 ? (available / totalBudget) * 100 : 0
  return { totalBudget, totalDraw, available, headroom }
}

export function CapacityDashboard({ siteId, onOpenDevice }: Props) {
  const [data, setData] = useState<CapacityData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get<CapacityData>(`/sites/${siteId}/capacity`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  if (loading) {
    return (
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Capacity</div>
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton" style={{ height: 180, borderRadius: 'var(--radius-lg)' }} />
          ))}
        </div>
      </div>
    )
  }

  if (!data) return null

  const cameraCount = data.cameras.length
  const totalStorageTB = data.nvrs.reduce((s, d) => s + (d.actual_storage_tb || d.spec_storage_tb || 0), 0)
  const usedStorageTB = data.nvrs.reduce((s, d) => s + (d.storage_used_tb || 0), 0)
  const retention = calculateRetention(totalStorageTB, usedStorageTB, cameraCount)

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Capacity</div>
      <div className="grid grid-cols-3 gap-4">
        {/* NVR Storage & Retention */}
        <div className="card" style={{ cursor: 'pointer' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>NVR Storage & Retention</div>
          {data.nvrs.length > 0 ? (
            <>
              {data.nvrs.map(nvr => {
                const total = nvr.actual_storage_tb || nvr.spec_storage_tb || 0
                const used = nvr.storage_used_tb || 0
                return (
                  <div key={nvr.id} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      {nvr.label || nvr.hostname || (nvr.spec_brand ? `${nvr.spec_brand} ${nvr.spec_model}` : nvr.device_model) || 'NVR'}
                    </div>
                    <CapacityBar
                      used={used}
                      max={total}
                      label="Storage"
                      unit="TB"
                      color="var(--color-networking)"
                    />
                  </div>
                )
              })}
              <div style={{
                marginTop: 8, padding: '10px 12px', background: 'var(--bg-inset)',
                borderRadius: 'var(--radius)', fontSize: 12,
              }}>
                <div className="flex justify-between" style={{ marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-tertiary)' }}>{cameraCount} cameras recording</span>
                </div>
                <div className="flex justify-between" style={{ marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-tertiary)' }}>Daily storage</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{retention.dailyGB.toFixed(0)} GB/day</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-tertiary)' }}>Est. retention</span>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontWeight: 700,
                    color: retention.retentionDays < 14 ? 'var(--danger)' : 'var(--success)',
                  }}>
                    {retention.retentionDays.toFixed(1)} days
                  </span>
                </div>
                {retention.retentionDays < 14 && retention.retentionDays > 0 && (
                  <div style={{
                    marginTop: 8, padding: '6px 10px', background: 'var(--err-m)',
                    borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: 11, fontWeight: 500,
                  }}>
                    Below 14-day target — add storage or reduce bitrate
                  </div>
                )}
              </div>
            </>
          ) : (
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No NVR/VMS configured</p>
          )}
        </div>

        {/* PoE Budget per Switch */}
        <div className="card" style={{ cursor: 'pointer' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>PoE Budget</div>
          {data.switches.length > 0 ? data.switches.map(sw => {
            const ports = data.switchPorts[sw.id] || []
            const poeBudget = sw.spec_poe_budget_watts || sw.poe_budget_watts || 0
            const budget = calculatePoeBudget(poeBudget, ports)
            const connectedPorts = ports.filter((p: PortRow) => p.status === 'connected' || p.connected_device_id)
            const switchLabel = sw.label || sw.hostname || (sw.spec_brand ? `${sw.spec_brand} ${sw.spec_model}` : sw.device_model) || 'Switch'

            return (
              <div key={sw.id} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {switchLabel}
                </div>
                {budget.totalBudget > 0 && (
                  <CapacityBar
                    used={budget.totalDraw}
                    max={budget.totalBudget}
                    label="PoE"
                    unit="W"
                    color="var(--warning)"
                    warnAt={0.7}
                  />
                )}
                {connectedPorts.length > 0 && (
                  <div style={{
                    marginTop: 4, padding: '6px 10px', background: 'var(--bg-inset)',
                    borderRadius: 'var(--radius)', fontSize: 11, color: 'var(--text-tertiary)',
                  }}>
                    {connectedPorts.length} device{connectedPorts.length !== 1 ? 's' : ''} connected
                    {budget.available > 0 && ` · ${budget.available.toFixed(0)}W available`}
                  </div>
                )}
              </div>
            )
          }) : (
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No switches configured</p>
          )}
        </div>

        {/* Switch Port Utilization */}
        <div className="card" style={{ cursor: 'pointer' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Port Utilization</div>
          {data.switches.length > 0 ? data.switches.map(sw => {
            const ports = data.switchPorts[sw.id] || []
            const totalPorts = sw.spec_total_ports || sw.port_count || 24
            const connected = ports.filter((p: PortRow) => p.status === 'connected' || p.connected_device_id).length
            const uplinks = ports.filter((p: PortRow) => p.status === 'uplink').length
            const available = totalPorts - connected - uplinks
            const switchLabel = sw.label || sw.hostname || (sw.spec_brand ? `${sw.spec_brand} ${sw.spec_model}` : sw.device_model) || 'Switch'

            return (
              <div key={sw.id} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {switchLabel}
                </div>
                <CapacityBar
                  used={connected + uplinks}
                  max={totalPorts}
                  label="Ports"
                  unit="ports"
                  color="var(--primary)"
                />
                <div style={{
                  fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2,
                }}>
                  Connected: {connected} · Available: {available} · Uplink: {uplinks}
                </div>
                <PortMapVisual
                  totalPorts={totalPorts}
                  ports={ports}
                  onPortClick={(port) => {
                    if (port.connected_device_id && onOpenDevice) {
                      // would need slug lookup
                    }
                  }}
                />
              </div>
            )
          }) : (
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No switches configured</p>
          )}
        </div>
      </div>
    </div>
  )
}
